import { ValidationError } from "sequelize"
import CryptoJs from "crypto-js"

import {
    MtUser,
    MtUserCreate,
    MtUserSave,
    MtUserChangeStatus,
    MtUserChangePassword,
    MtUserChangeConfirmationCode,
    MtUserRegistered,
    MtUserRegisteredCreate,
} from "server/models"
import { ACTIVE, INACTIVE } from "const"
import { ActiveInactive, OrderType } from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import { LangType, UserValidation, UserRegisteredValidation } from "client/store"
import {
    UserList,
    UserFilters,
} from "./types"


export const LoadAllUsers = () => (
    GET("/mtUser/all")
    .then((users: MtUser[]) => {
        const userList: UserList = {}
        users.forEach((user) => {
            userList[user.id] = user
        })
        return userList
    })
)

export const LoadUserById = (userId: number) => (
    GET(`/mtUser/id/${userId}`)
    .then((user: MtUser) => user)
)

export const LoadUserByLoginOrEmail = (value: string) => (
    GET(`/mtUser/loginOrEmail/${value}`)
    .then((user: MtUser) => user)
)

export const LoadUserByConfirmationCode = (confirmationCode: string) => (
    GET(`/mtUser/confirmationCode/${confirmationCode}`)
    .then((user: MtUser) => user)
)

export const SaveUser = (
    id: number,
    user: MtUserCreate,
    lang: LangType,
) => {
    const data: MtUserSave = {
        id,
        user,
    }
    return POST(
        "/mtUser/save",
        JSON.stringify(data),
    ).then((res) => {
        const newUser = res as MtUser
        const error = res as ValidationError
        return {
            newUserId: newUser.id || null,
            validation: <UserValidation>validate(error, lang),
        }
    })
}

export const ChangeUserStatus = (user: MtUser) => {
    const status: ActiveInactive = user.status === ACTIVE ? INACTIVE : ACTIVE
    const data: MtUserChangeStatus = {
        id: user.id,
        status,
    }
    return POST(
        "/mtUser/changeStatus",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
            ? status
            : user.status
    ))
}

export const ChangeUserPassword = (userId: number, password: string) => {
    const data: MtUserChangePassword = {
        id: userId,
        password: EncryptUserPassword(password),
    }
    return POST(
        "/mtUser/changePassword",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
    ))
}

export const ChangeUserConfirmationCode = (userId: number, confirmationCode: string) => {
    const data: MtUserChangeConfirmationCode = {
        id: userId,
        confirmationCode,
    }
    return POST(
        "/mtUser/changeConfirmationCode",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
    ))
}

export const CheckPasswordRestrictions = (password: string) => {
    // minimum eight characters, at least one letter, one number and punctuation marks (,.<>/?;:'"[]{}\|`~!@#$%^&*()_+=-)
    // eslint-disable-next-line react/destructuring-assignment, max-len
    const matchArray = password.match(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[,.<>/?;:'"[\]{}\\|`~!@#$%^&*()_+=-])[A-Za-z\d,.<>/?;:'"[\]{}\\|`~!@#$%^&*()_+=-]{8,}$/)
    return matchArray !== null
}

export const EncryptUserPassword = (password: string) => (
    CryptoJs.SHA256(password).toString()
)

export const CreateConfirmationCodeByEmail = (email: string) => (
    CryptoJs.MD5(email + new Date().getTime()).toString()
)

export const SortUsers = (
    users: MtUser[],
    sortColumn: keyof MtUser,
    sortOrder: OrderType,
    filters: UserFilters,
): number[] => {
    const usersFiltered = users.filter((user) => {
        let showRow = true
        if (filters.status && user.status !== filters.status) showRow = false
        else if (filters.search) showRow = search(filters.search, user)
        return showRow
    })
    usersFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))

    const userIds: number[] = []
    usersFiltered.forEach((user) => userIds.push(user.id))

    return userIds
}

// registered users

export const LoadRegisteredUserByEmail = (email: string) => (
    GET(`/mtUserRegistered/email/${email}`)
    .then((user: MtUserRegistered) => user)
)

export const LoadRegisteredUserByCode = (confirmationCode: string) => (
    GET(`/mtUserRegistered/confirmationCode/${confirmationCode}`)
    .then((user: MtUserRegistered) => user)
)

export const RegisterUser = (
    user: MtUserRegisteredCreate,
    lang: LangType,
) => (
    POST(
        "/mtUserRegistered/add",
        JSON.stringify(user),
    ).then((res) => {
        const newUser = res as MtUserRegistered
        const error = res as ValidationError
        return {
            newUser,
            validation: <UserRegisteredValidation>validate(error, lang),
        }
    })
)

export const DeleteRegisteredUser = (email: string) => (
    DELETE(`/mtUserRegistered/delete/${email}`)
    .then((removedRows: number) => removedRows)
)
